import React, { useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as Yup from 'yup'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import InputAdornment from '@material-ui/core/InputAdornment'
import CircularProgress from '@material-ui/core/CircularProgress'
import Link from '@material-ui/core/Link'
import MailOutlineIcon from 'components/material-symbol/icons/MailOutline'
import CheckCircleOutlineIcon from 'components/material-symbol/icons/CheckCircleOutline'
import useStyles from './styles'
import { ReactComponent as PgaLogo } from 'images/pga-seal-icon.svg'

const SEND_GOLFER_CLAIM_LINK = gql`
  mutation SendGolferClaimLink($input: SendGolferClaimLinkInput!) {
    sendGolferClaimLink(input: $input) {
      success
      message
    }
  }
`

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email address is required'),
})

const AchievementsLoginPage = () => {
  const classes = useStyles()

  const [emailSent, setEmailSent] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')

  const {
    handleSubmit,
    control,
    watch,
    errors,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  })

  const [sendClaimLink, { loading }] = useMutation(SEND_GOLFER_CLAIM_LINK, {
    onCompleted: () => {
      setEmailSent(true)
    },
    onError: () => {
      // Always show success to prevent email enumeration
      setEmailSent(true)
    },
  })

  const onSubmit = async data => {
    setSubmittedEmail(data.email)
    await sendClaimLink({
      variables: {
        input: {
          email: data.email,
        },
      },
      context: { skipAuth: true },
    })
  }

  const isLoading = isSubmitting || loading
  const emailValue = watch('email', '')
  const hasValidEmail = validationSchema.isValidSync({ email: emailValue })

  return (
    <Box className={classes.root}>
      <Box className={classes.logoContainer}>
        <PgaLogo className={classes.logo} aria-label="PGA of America" />
        <Box className={classes.taglineDivider} />
        <Typography className={classes.tagline}>We love this Game</Typography>
      </Box>

      <Box className={classes.card}>
        {!emailSent ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Typography className={classes.title}>Welcome Back</Typography>
            <Typography className={classes.description}>
              Enter your email address to receive a magic link. We'll verify
              your identity and get you logged in instantly.
            </Typography>

            <Typography className={classes.inputLabel}>
              EMAIL ADDRESS
            </Typography>
            <Controller
              name="email"
              control={control}
              render={({ onChange, onBlur, value, ref }) => (
                <TextField
                  type="email"
                  placeholder="you@example.com"
                  variant="outlined"
                  fullWidth
                  value={value || ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  inputRef={ref}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  className={classes.textField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineIcon className={classes.emailIcon} />
                      </InputAdornment>
                    ),
                  }}
                  disabled={isLoading}
                />
              )}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              className={classes.submitButton}
              disabled={isLoading || !hasValidEmail}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'SEND LOGIN LINK'
              )}
            </Button>

            <Typography className={classes.helpLink}>
              Need help?{' '}
              <Link
                href="mailto:support@pga.com"
                className={classes.supportLink}
              >
                Contact Support
              </Link>
            </Typography>
          </form>
        ) : (
          <Box className={classes.successMessage}>
            <CheckCircleOutlineIcon className={classes.successIcon} />
            <Typography className={classes.successTitle}>
              Check Your Email
            </Typography>
            <Typography className={classes.successDescription}>
              We've sent a magic link to <strong>{submittedEmail}</strong>.
              Click the link in the email to sign in to your account.
            </Typography>
            <Typography
              className={classes.successDescription}
              style={{ marginTop: 16 }}
            >
              Didn't receive the email? Check your spam folder or{' '}
              <Link
                href="#"
                onClick={e => {
                  e.preventDefault()
                  setEmailSent(false)
                }}
                className={classes.supportLink}
              >
                try again
              </Link>
              .
            </Typography>
          </Box>
        )}
      </Box>

      <Typography className={classes.footer}>
        © {new Date().getFullYear()} PGA of America. All rights reserved.
      </Typography>
    </Box>
  )
}

export default AchievementsLoginPage
